<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="60vh"
      scrollable
    >
      <v-card>
        <v-card-title>
          Document Types
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            class="mr-4"
          ></v-text-field>
          <v-btn
            icon
            @click="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            height="60vh"
            :headers="tableHeaders"
            :items="documentsTypes"
            :search="search"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <template v-slot:[`item.Name`]="{ item }">
              <v-text-field
                dense
                hide-details="auto"
                :rules="[rules.required, rules.alreadyExist, rules.englishOnly]"
                v-if="editMode && currentItem && currentItem.Type_ID === item.Type_ID"
                v-model="currentItem.Name"
              ></v-text-field>
              <span v-else>{{ item.Name }}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item }">
              <v-btn
                :disabled="editMode && (currentItem && currentItem.Type_ID !== item.Type_ID) || (currentItem && currentItem.Type_ID === item.Type_ID && canUpdate)"
                :color="editMode && currentItem && currentItem.Type_ID === item.Type_ID ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item)"
              >
                <v-icon>
                  {{ editMode && currentItem && currentItem.Type_ID === item.Type_ID ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="!editMode || (currentItem && currentItem.Type_ID !== item.Type_ID)"
                :disabled="!item.Can_Delete || editMode"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode && currentItem && currentItem.Type_ID === item.Type_ID"
                color="primary"
                small
                icon
                @click="onCancelClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            :disabled="editMode"
            @click="createNewTypeDialog = !createNewTypeDialog"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      v-model="createNewTypeDialog"
    >
      <v-card>
        <v-card-title>
          New Document Type
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="createNewTypeDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newTypeName"
            label="Type Name"
            autofocus
            :rules="[rules.required, rules.alreadyExist, rules.englishOnly]"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="canSave"
            @click="createNewDocumentsType"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteTypeDialog"
      v-model="deleteTypeDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteDocumentsType"
      @onCancelClick="cancelDelete"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
export default {
  name: 'EmployeeDocumentsTypesManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      search: '',
      currentItem: null,
      createNewTypeDialog: false,
      documentsTypes: [],
      deleteTypeDialog: false,
      editMode: false,
      newTypeName: '',
      tableHeaders: [
        {
          text: 'Type Name',
          value: 'Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ],
      rules: {
        required: v => !!v || 'Required',
        alreadyExist: (val) => this.validateOnAlreadyExist(val) || 'The type name already exists',
        englishOnly: v => {
          if (!v) return true
          return !/[а-яА-ЯЁёҐґЄєІіЇї]/.test(v) || 'English only'
        }
      }
    }
  },
  mounted () {
    this.getDocumentsTypes()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    canUpdate () {
      if (!this.currentItem || !this.currentItem.Name) return true
      const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(this.currentItem.Name)
      return !this.currentItem.Name?.trim().length ||
             !this.validateOnAlreadyExist(this.currentItem.Name, this.currentItem.Type_ID) ||
             hasCyrillic
    },
    canSave () {
      const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(this.newTypeName)
      return !this.newTypeName ||
             !this.newTypeName?.trim().length ||
             !this.validateOnAlreadyExist(this.newTypeName) ||
             hasCyrillic
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    }
  },
  methods: {
    validateOnAlreadyExist (val, id = null) {
      if (!val) return true
      return !this.documentsTypes.some(el =>
        el.Name.toLowerCase() === val.trim().toLowerCase() && el.Type_ID !== id
      )
    },
    onEditClick (item) {
      if (this.editMode) {
        this.updateDocumentsType()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
        this.editMode = true
      }
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.tableHeaders[0].sortable = true
    },
    onDeleteClick (item) {
      this.deleteTypeDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteTypeDialog = false
      this.currentItem = null
    },
    getDocumentsTypes () {
      this.axios.get('/api/document_type_to_manage', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.documentsTypes = res.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    createNewDocumentsType () {
      this.axios.post('/api/document_type', {
        TypeName: this.newTypeName.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.createNewTypeDialog = false
        this.newTypeName = ''
        this.getDocumentsTypes()
        this.$store.dispatch('setSuccess', { successMsg: 'New document type successfully created' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateDocumentsType () {
      this.axios.put(`/api/document_type/${this.currentItem.Type_ID}`, {
        TypeName: this.currentItem.Name.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getDocumentsTypes()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'Type successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteDocumentsType () {
      this.axios.delete(`/api/document_type/${this.currentItem.Type_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getDocumentsTypes()
        this.cancelDelete()
        this.$store.dispatch('setSuccess', { successMsg: 'Type successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
